import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import OperationalProcessesReportApi from "../../../../infrastructure/api/operationalProcessesReport";
import useGetPageInfo from "../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../components/shared/table";
import {
  LOAN_PROCESS_STATUSES,
  LOAN_PROCESS_TYPES,
} from "../../../components/request/case-tabs/lendingOperationsConstants";
import {
  MEHRSAN_PROCESS_STATUSES,
  MEHRSAN_PROCESS_TYPES,
} from "../../../components/request/case-tabs/mehrsanOperationsConstants";
import { toJalaliFullDate } from "../../../middleware/middleware";
import {
  ensureRowSla,
  normalizeReportRow,
  SLA_FILTER_OPTIONS,
} from "./operationalProcessesReportUtils";
import AllRequestsCaseDrawer from "../solarent-operator/all-requests/AllRequestsCaseDrawer";
import {
  MANAGEMENT_CASE_DETAIL_API,
  MANAGEMENT_CASE_HISTORY_API,
} from "../management/all-request/index";

const OPERATION_MODULES = [
  { value: "all", label: "همه" },
  { value: "mehrsan", label: "مهرسان" },
  { value: "lending", label: "وام" },
];

const initialFilters = {
  operationModule: "all",
  status: "",
  processType: "",
  lendingPartnerId: "",
  lastActionFrom: "",
  lastActionTo: "",
  inactiveDays: "",
  lastActionByUserId: "",
  assignedToUserId: "",
  slaStatus: "",
};

const buildSearchPayload = (values) => {
  const payload = {
    operationModule: values.operationModule || "all",
  };
  if (values.status !== "" && values.status != null) payload.status = Number(values.status);
  if (values.processType !== "" && values.processType != null) {
    payload.processType = Number(values.processType);
  }
  if (values.lendingPartnerId !== "" && values.lendingPartnerId != null) {
    payload.lendingPartnerId = Number(values.lendingPartnerId);
  }
  if (values.lastActionFrom) payload.lastActionFrom = values.lastActionFrom;
  if (values.lastActionTo) payload.lastActionTo = values.lastActionTo;
  if (values.inactiveDays !== "" && values.inactiveDays != null) {
    payload.inactiveDays = Number(values.inactiveDays);
  }
  if (values.lastActionByUserId !== "" && values.lastActionByUserId != null) {
    payload.lastActionByUserId = Number(values.lastActionByUserId);
  }
  if (values.assignedToUserId !== "" && values.assignedToUserId != null) {
    payload.assignedToUserId = Number(values.assignedToUserId);
  }
  return payload;
};

const OperationalProcessesReportPage = ({
  operationsSource = "Management",
  detailApi,
  historyApi,
  pageTitle = "گزارش عملیات باز پرونده‌ها",
}) => {
  const pageInfo = useGetPageInfo();
  const [filterLoading, setFilterLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filterValues, setFilterValues] = useState(initialFilters);
  const [filterOptions, setFilterOptions] = useState({
    experts: [],
    lendingPartners: [],
  });
  const [rows, setRows] = useState([]);
  const [caseDrawer, setCaseDrawer] = useState({ open: false, id: null, row: null });

  const resolvedDetailApi =
    detailApi ||
    (operationsSource === "Management"
      ? MANAGEMENT_CASE_DETAIL_API
      : "SLN/SolarentOperator/AllRequests");
  const resolvedHistoryApi =
    historyApi ||
    (operationsSource === "Management"
      ? MANAGEMENT_CASE_HISTORY_API
      : "SLN/SolarentOperator/CheckNewRequest");

  const statusOptions = useMemo(() => {
    if (filterValues.operationModule === "mehrsan") {
      return MEHRSAN_PROCESS_STATUSES.filter((s) => s.value !== 4 && s.value !== 5);
    }
    if (filterValues.operationModule === "lending") {
      return LOAN_PROCESS_STATUSES.filter(
        (s) => ![5, 6, 7, 8, 9].includes(s.value)
      );
    }
    return [];
  }, [filterValues.operationModule]);

  const processTypeOptions = useMemo(() => {
    if (filterValues.operationModule === "mehrsan") return MEHRSAN_PROCESS_TYPES;
    if (filterValues.operationModule === "lending") return LOAN_PROCESS_TYPES;
    return [];
  }, [filterValues.operationModule]);

  const headCells = useMemo(
    () => [
      {
        id: "operationModuleTitle",
        label: "نوع عملیات",
        type: "chip",
        model: { colorField: "moduleChipColor" },
      },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "processTypeTitle", label: "نوع فرآیند" },
      {
        id: "currentStatusTitle",
        label: "وضعیت",
        type: "chip",
        model: { colorField: "statusColor" },
      },
      {
        id: "slaStatusTitle",
        label: "وضعیت SLA",
        type: "chip",
        model: { colorField: "slaColor" },
      },
      { id: "referenceDisplay", label: "مرجع / کد رهگیری" },
      { id: "lastActionAtDisplay", label: "آخرین اقدام" },
      { id: "lastActionByFullName", label: "کارشناس آخرین اقدام" },
      { id: "assignedToFullName", label: "مسئول فرآیند" },
      { id: "openDays", label: "روز بدون اقدام", type: "number" },
    ],
    []
  );

  const tableRows = useMemo(() => {
    const mapped = rows.map((raw) => {
      const row = ensureRowSla(normalizeReportRow(raw));
      const refParts = [
        row.referenceNumber,
        row.trackingCode,
        row.lendingPartnerTitle,
      ].filter(Boolean);
      return {
        ...row,
        id: `${row.operationModule || "row"}-${row.processId}`,
        moduleChipColor:
          row.operationModule === "lending" ? "secondary" : "primary",
        referenceDisplay: refParts.length ? refParts.join(" · ") : "—",
        assignedToFullName: row.assignedToFullName || "—",
        lastActionAtDisplay: row.lastActionAt
          ? toJalaliFullDate(row.lastActionAt)
          : "—",
      };
    });

    const slaFilter = filterValues.slaStatus;
    if (!slaFilter) return mapped;
    return mapped.filter((row) => row.slaStatus === slaFilter);
  }, [rows, filterValues.slaStatus]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setFilterLoading(true);
        const response = await OperationalProcessesReportApi.getFilterData(
          operationsSource
        );
        if (response?.isSuccess) {
          setFilterOptions({
            experts: response.data?.experts || response.data?.Experts || [],
            lendingPartners:
              response.data?.lendingPartners ||
              response.data?.LendingPartners ||
              [],
          });
        } else {
          toast.error(
            response?.message || "دسترسی به فیلترهای گزارش مجاز نیست"
          );
          setFilterOptions({ experts: [], lendingPartners: [] });
        }
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "خطا در دریافت فیلترهای گزارش";
        toast.error(msg);
        setFilterOptions({ experts: [], lendingPartners: [] });
      } finally {
        setFilterLoading(false);
      }
    };
    loadFilters();
  }, [operationsSource]);

  const onSearch = useCallback(async () => {
    try {
      setLoading(true);
      const response = await OperationalProcessesReportApi.search(
        operationsSource,
        buildSearchPayload(filterValues)
      );
      if (response?.isSuccess) {
        setRows(response.data || []);
      } else {
        toast.error(response?.message || "خطا در دریافت گزارش");
        setRows([]);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "خطا در دریافت گزارش";
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [operationsSource, filterValues]);

  const onClearFilters = () => {
    setFilterValues(initialFilters);
    setRows([]);
  };

  const onOpenCase = useCallback((row) => {
    const normalized = normalizeReportRow(row);
    const requestId = normalized.requestInfoId;
    if (!requestId) return;
    setCaseDrawer({
      open: true,
      id: requestId,
      row: {
        id: requestId,
        referenceID: normalized.requestTrackingCode,
        fullName: normalized.applicantFullName,
        mobile: normalized.applicantMobile,
      },
    });
  }, []);

  const showLendingPartner =
    filterValues.operationModule === "lending" ||
    filterValues.operationModule === "all";
  const showStatusType =
    filterValues.operationModule === "mehrsan" ||
    filterValues.operationModule === "lending";

  return (
    <Box>
      <BreadcrumbsComponent
        pageTitle={pageInfo?.title || pageTitle}
        pageDescription={pageInfo?.description}
      />

      <Card sx={{ mb: 2 }}>
        <CardHeader title="فیلترها" />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت SLA</InputLabel>
                <Select
                  label="وضعیت SLA"
                  value={filterValues.slaStatus}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      slaStatus: e.target.value,
                    }))
                  }
                >
                  {SLA_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع عملیات</InputLabel>
                <Select
                  label="نوع عملیات"
                  value={filterValues.operationModule}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      operationModule: e.target.value,
                      status: "",
                      processType: "",
                      lendingPartnerId: "",
                    }))
                  }
                >
                  {OPERATION_MODULES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {showStatusType ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>وضعیت</InputLabel>
                    <Select
                      label="وضعیت"
                      value={filterValues.status}
                      onChange={(e) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">همه</MenuItem>
                      {statusOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>نوع فرآیند</InputLabel>
                    <Select
                      label="نوع فرآیند"
                      value={filterValues.processType}
                      onChange={(e) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          processType: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">همه</MenuItem>
                      {processTypeOptions.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : null}

            {showLendingPartner ? (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" disabled={filterLoading}>
                  <InputLabel>لندتک</InputLabel>
                  <Select
                    label="لندتک"
                    value={filterValues.lendingPartnerId}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        lendingPartnerId: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {filterOptions.lendingPartners.map((p) => (
                      <MenuItem key={p.id ?? p.Id} value={p.id ?? p.Id}>
                        {p.title ?? p.Title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}

            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="از تاریخ آخرین اقدام"
                fullWidth
                size="small"
                value={filterValues.lastActionFrom}
                onChange={(value) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    lastActionFrom: value || "",
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <JalaliDatePicker
                label="تا تاریخ آخرین اقدام"
                fullWidth
                size="small"
                value={filterValues.lastActionTo}
                onChange={(value) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    lastActionTo: value || "",
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="بدون اقدام بیش از (روز)"
                value={filterValues.inactiveDays}
                onChange={(e) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    inactiveDays: e.target.value,
                  }))
                }
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" disabled={filterLoading}>
                <InputLabel>کارشناس آخرین اقدام</InputLabel>
                <Select
                  label="کارشناس آخرین اقدام"
                  value={filterValues.lastActionByUserId}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      lastActionByUserId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {filterOptions.experts.map((u) => (
                    <MenuItem key={u.id ?? u.Id} value={u.id ?? u.Id}>
                      {u.fullName ?? u.FullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" disabled={filterLoading}>
                <InputLabel>مسئول فرآیند</InputLabel>
                <Select
                  label="مسئول فرآیند"
                  value={filterValues.assignedToUserId}
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      assignedToUserId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {filterOptions.experts.map((u) => (
                    <MenuItem key={`owner-${u.id ?? u.Id}`} value={u.id ?? u.Id}>
                      {u.fullName ?? u.FullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
              onClick={onSearch}
              disabled={loading}
            >
              جستجو
            </Button>
            <Button
              variant="outlined"
              startIcon={<HighlightOffIcon />}
              onClick={onClearFilters}
              disabled={loading}
            >
              پاک کردن
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={pageTitle}
          subheader={
            tableRows.length > 0
              ? `${tableRows.length} فرآیند${
                  filterValues.slaStatus ? " (فیلتر SLA)" : ""
                } — مرتب‌سازی: قدیمی‌ترین اقدام در بالا`
              : rows.length > 0
              ? "هیچ ردیفی با فیلتر SLA انتخاب‌شده مطابقت ندارد"
              : "برای مشاهده نتایج، جستجو را بزنید"
          }
        />
        <Divider />
        <CardContent sx={{ pt: 0 }}>
          <EnhancedTable
            rowKey="id"
            headCells={headCells}
            rows={tableRows}
            loading={loading}
            showFilter={false}
            actionsMode="menu"
            getRowStatus={(row) => (row.slaStatus === "overdue" ? "error" : "none")}
            actions={[
              {
                icon: <FolderOpenIcon />,
                onClick: onOpenCase,
                tooltip: "مشاهده پرونده",
              },
            ]}
          />
        </CardContent>
      </Card>

      <AllRequestsCaseDrawer
        open={caseDrawer.open}
        requestId={caseDrawer.id}
        listRow={caseDrawer.row}
        onClose={() => setCaseDrawer({ open: false, id: null, row: null })}
        detailApi={resolvedDetailApi}
        historyApi={resolvedHistoryApi}
      />
    </Box>
  );
};

OperationalProcessesReportPage.propTypes = {
  operationsSource: PropTypes.oneOf(["Management", "SolarentOperator"]),
  detailApi: PropTypes.string,
  historyApi: PropTypes.string,
  pageTitle: PropTypes.string,
};

export default OperationalProcessesReportPage;
